import type { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';
import { SectionLabel } from '../layout/Typography';
import {
  groupChangelogEntriesByArea,
  parseChangelogEntries,
  type ChangelogEntry,
} from './changelog-format';

/**
 * Per-category accent used by the changelog section label. Keys are the exact
 * section headings release-please writes into `CHANGELOG.md` ("Performance
 * Improvements", not "Performance"), so a section is never silently demoted to
 * the neutral fallback.
 */
const CATEGORY_ACCENTS: Record<string, { text: string; dot: string }> = {
  Features: { text: 'text-status-success', dot: 'bg-status-success' },
  'Bug Fixes': { text: 'text-status-error', dot: 'bg-status-error' },
  Performance: { text: 'text-status-warning', dot: 'bg-status-warning' },
  'Performance Improvements': { text: 'text-status-warning', dot: 'bg-status-warning' },
  Documentation: { text: 'text-status-accent-sky', dot: 'bg-status-accent-sky' },
  'Code Refactoring': { text: 'text-status-accent-sky', dot: 'bg-status-accent-sky' },
  Reverts: { text: 'text-status-error', dot: 'bg-status-error' },
  '\u26a0 BREAKING CHANGES': { text: 'text-status-warning', dot: 'bg-status-warning' },
};

const NEUTRAL_ACCENT = { text: 'text-muted-foreground', dot: 'bg-muted-foreground' };

export function categoryAccent(category: string): { text: string; dot: string } {
  return CATEGORY_ACCENTS[category] ?? NEUTRAL_ACCENT;
}

/* ------------------------------------------------------------------ */
/* Composable layout primitives                                       */
/* ------------------------------------------------------------------ */

export interface ChangelogCategorySectionProps {
  name: string;
  children: ReactNode;
  className?: string;
}

/**
 * One conventional-changelog category ("Features", "Bug Fixes", …): a small
 * accent-colored label over its entries. Deliberately not a card — a release
 * already sits inside the page's own structure, and boxing every category
 * inside it stacked three borders around a one-line change.
 */
export function ChangelogCategorySection({
  name,
  children,
  className,
}: ChangelogCategorySectionProps) {
  const accent = categoryAccent(name);
  return (
    <section className={cn('space-y-1.5', className)}>
      {name && (
        <div className="flex items-center gap-2">
          <span className={cn('h-1.5 w-1.5 rounded-full', accent.dot)} aria-hidden="true" />
          <SectionLabel as="h4" className={accent.text}>
            {name}
          </SectionLabel>
        </div>
      )}
      {children}
    </section>
  );
}

export interface ChangelogListProps {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/** Spacing wrapper for a list of changelog entry rows. */
export function ChangelogList({ children, className, ...rest }: ChangelogListProps) {
  return (
    <ul className={cn('space-y-0.5', className)} {...rest}>
      {children}
    </ul>
  );
}

/**
 * Inline markdown inside a changelog entry (links, `code`, emphasis). The entry
 * is already a single sentence, so the paragraph wrapper collapses to a span
 * and headings/lists are not expected.
 */
const ENTRY_MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => <>{children}</>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary underline underline-offset-2 transition-opacity hover:opacity-80"
    >
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
  ),
};

export interface ChangelogAreaBadgeProps {
  area: string;
  className?: string;
}

/**
 * The product area an entry belongs to, as a quiet chip in front of the entry
 * text. Deliberately lighter than the category label above it
 * ({@link ChangelogCategorySection}): the category is the section, the area is
 * a per-row tag, and it should never out-shout the change it labels.
 */
export function ChangelogAreaBadge({ area, className }: ChangelogAreaBadgeProps) {
  return (
    <span
      className={cn(
        'mt-px shrink-0 rounded-md border border-border/40 bg-muted/40 px-1.5 py-0.5 text-2xs font-medium text-muted-foreground',
        className,
      )}
    >
      {area}
    </span>
  );
}

export interface ChangelogEntriesProps {
  /** Raw changelog markdown for one category (or one whole release). */
  content: string;
  className?: string;
}

/**
 * Renders a changelog body as one list of entries, each tagged with its product
 * area ("Agents", "Automations", …) in front of the text. Each entry has
 * its PR/commit references and its raw commit scope stripped by
 * {@link parseChangelogEntries} — the scope becomes the badge instead of a
 * `**web:**` prefix inside the sentence.
 *
 * Rows are ordered by area (see {@link groupChangelogEntriesByArea}), so
 * same-badge entries always sit next to each other and their text lines up,
 * which is what makes a 20-line release scannable without section headings.
 */
export function ChangelogEntries({ content, className }: ChangelogEntriesProps) {
  const groups = groupChangelogEntriesByArea(parseChangelogEntries(content));
  const entries: ChangelogEntry[] = groups.flatMap((group) => group.entries);
  if (entries.length === 0) return null;
  // A badge only tells you something when the section spans more than one area.
  // Stamping the same label on every row (or "General" on a body with no scopes
  // at all) is noise the reader has to look past.
  const showBadges = groups.length > 1;

  return (
    <ChangelogList className={className} data-testid="changelog-entries">
      {entries.map((entry, index) => (
        // Entry text is not unique (a release can repeat a bullet verbatim),
        // so the position in the list is what identifies a row.
        <li
          key={`${entry.area}:${index}`}
          className="-mx-2 flex items-start gap-2.5 rounded-lg px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-muted/30"
        >
          {showBadges ? <ChangelogAreaBadge area={entry.area} /> : null}
          <span className="leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={ENTRY_MARKDOWN_COMPONENTS}>
              {entry.text}
            </ReactMarkdown>
          </span>
        </li>
      ))}
    </ChangelogList>
  );
}

/* ------------------------------------------------------------------ */
/* Parsing utility (shared between consumers)                         */
/* ------------------------------------------------------------------ */

export interface ChangeCategory {
  name: string;
  content: string;
}

/**
 * Splits a markdown block into named categories by `### Heading` markers.
 * If the block has no `###` headings, returns a single unnamed category.
 */
export function splitByCategory(raw: string): ChangeCategory[] {
  // Anchor the capture to a non-space (`\S`) right after the heading's spaces so
  // the leading `[ \t]+` and the captured `.*` never compete for the same
  // characters. The previous `\s+(.+)` let both quantifiers match runs of
  // spaces, giving the engine quadratic backtracking on adversarial input
  // (CodeQL js/polynomial-redos). Same result for real `### Heading` lines.
  const categoryRegex = /^###[ \t]+(\S.*)$/gm;
  const matches = [...raw.matchAll(categoryRegex)];
  if (matches.length === 0) return [{ name: '', content: raw }];

  const categories: ChangeCategory[] = [];
  for (let i = 0; i < matches.length; i++) {
    const name = matches[i][1].trim();
    const start = matches[i].index! + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index! : raw.length;
    const content = raw.slice(start, end).trim();
    categories.push({ name, content });
  }
  return categories;
}
